import { AppDate } from '../envTypes';
import { ModelBase } from './base';
import { PaymentMethodDetails } from './order';
import { PopStoreCreatorBusinessType } from './profile';

/**
 * Ref: https://docs.stripe.com/api/subscriptions/object
 */
export enum SubscriptionStatus {
  active = 'active',
  canceled = 'canceled',
  incomplete = 'incomplete',
  incomplete_expired = 'incomplete_expired',
  trialing = 'trialing',
  past_due = 'past_due',
  unpaid = 'unpaid',
  paused = 'paused',
}

export enum SaasSubscriptionCouponType {
  PercentOff = 'percent_off',
  AmountOff = 'amount_off',
  Trial = 'trial',
}

export enum SassSubscriptionCouponDurationType {
  Once = 'once',
  Repeating = 'repeating',
  Forever = 'forever',
}

export enum SaasSubscriptionCouponFrequency {
  Monthly = 'monthly',
  Yearly = 'yearly',
}

export enum SaasSubscriptionCouponSource {
  /** Created by an operator by hand. The only source the admin list shows. */
  Manual = 'manual',
  /** Generated for one seller as a cancellation save. */
  Retention = 'retention',
  /**
   * A plan's standing offer, whose code goes into `public_config` instead of
   * to a seller. It has its own value rather than reusing one of the above:
   * `manual` is what the admin list queries for, and this is not a coupon an
   * operator manages one by one, while `retention` is read back to find a
   * seller's reusable save offer and to decide whether they have taken one.
   */
  PlanCoupon = 'plan_coupon',
  /**
   * Minted for one creator when a coupon campaign's email stage runs, from the
   * stage's template coupon.
   *
   * Its own value for the same reason `plan_coupon` has one: `manual` is what
   * the admin list queries for, and these are generated per recipient rather
   * than managed one by one — a campaign can mint thousands. `retention` is
   * read back to find a seller's reusable save offer and to decide whether
   * they have taken one, which a campaign coupon must not answer.
   */
  Campaign = 'campaign',
  /**
   * Minted for one creator who cancelled, shortly before their access ends,
   * for the win-back email's "come back for N% off" link. Its own value so
   * the two save offers stay distinguishable in the data (the admin list,
   * the cancel flow's reusable-coupon lookup); for the "one save offer per
   * seller, lifetime" rule it counts exactly like `retention` — having
   * redeemed either, the seller is offered neither again.
   */
  WinBack = 'win_back',
}

/**
 * Which of the cancellation flow's two saves a `retention` coupon is.
 *
 * Both keep `source: retention` on purpose: `hasRedeemedRetentionCoupon`
 * reads that back to decide whether a seller has taken ANY save, and one
 * taken means neither is offered again. This is then the only thing telling
 * them apart — the apply endpoint grants the feedback reward's credits for
 * `free_month` and nothing extra for `discount`.
 */
export enum SaasSubscriptionRetentionOffer {
  /** A percentage off the next few invoices, shown with the first survey answer. */
  Discount = 'discount',
  /** One invoice free plus bonus credits, offered for the full survey. */
  FreeMonth = 'free_month',
}

/**
 * What a seller cancelling an ECHO-ME plan or trial chose to happen when it
 * ends (POP-364). `DowngradeToFree` keeps the account on the Free (Starter)
 * plan, as every ending did before the choice existed; `Cancel` ends it with no
 * Free plan fallback. Absent on a cancel that asked nothing: other plan
 * families, admin cancels, older clients.
 */
export enum SaasSubscriptionCancelIntent {
  DowngradeToFree = 'downgrade_to_free',
  Cancel = 'cancel',
}

/**
 * How a coupon came to be applied to one seller, recorded on the redemption
 * rather than on the coupon.
 *
 * Orthogonal to {@link SaasSubscriptionCouponSource}, which says how the
 * coupon was CREATED and has one value for the whole coupon. The same coupon
 * can reach one seller as the plan's standing offer and another because they
 * typed the code, so this belongs per redemption.
 */
export enum SaasSubscriptionCouponAppliedVia {
  /** The plan's standing offer from `public_config`, applied by the system. */
  PlanCoupon = 'plan-coupon',
  /** A code the seller typed, which is not the plan's standing offer. */
  UserSupplied = 'user-supplied',
  /**
   * A code the seller typed that IS the plan's standing offer. The code is no
   * longer returned to sellers, so this should be near-zero: a non-zero count
   * means the code is circulating somewhere it should not be.
   */
  UserSuppliedPlanCoupon = 'user-supplied-plan-coupon',
  /** The default trial coupon the subscription service substitutes itself. */
  DefaultTrial = 'default-trial',
}

export interface SaasSubscriptionCoupon extends ModelBase {
  name: string;
  applyToUsers?: string[]; // userId list
  applyToSaasSubscriptions?: string[]; // saasSubscriptionId list
  linkInBioUrls?: string[];
  type: SaasSubscriptionCouponType;
  amountOff?: number;
  percentOff?: number;
  duration?:
    | {
        type:
          | SassSubscriptionCouponDurationType.Once
          | SassSubscriptionCouponDurationType.Forever;
      }
    | {
        type: SassSubscriptionCouponDurationType.Repeating;
        durationInMonths?: number; // only for repeating duration
      };
  maxRedemptions?: number; // Maximum number of times this coupon can be redeemed, in total, across all customers, before it is no longer valid.
  redeemExpireTime?: AppDate; // Date after which the coupon can no longer be redeemed.
  timesRedeemed?: number; // Number of times this coupon has been applied to a customer.
  isValid: boolean; // Taking account of the above properties, whether this coupon can still be applied to a customer.
  isDeleted?: boolean;
  createdBy: string;
  source?: SaasSubscriptionCouponSource;
  /** Set on every `source: retention` coupon minted since the two-offer flow; absent on older ones, which were all discounts. */
  retentionOffer?: SaasSubscriptionRetentionOffer;
  couponFrequency?: SaasSubscriptionCouponFrequency;
  /**
   * When trial ends, we will charge the subscription fee automatically
   */
  trialPeriodDays?: number;
}

export enum SaasSubscriptionScene {
  Checkout = 'checkout',
  Welcome = 'welcome',
  WinBack = 'winback',
  AvatarMe = 'avatar_me',
  AdvancedStyle = 'advanced_style',
  CommentChat = 'comment_chat',
  DigitalDownloads = 'digital_downloads',
  Followers = 'followers',
  MeetingSlots = 'meeting_slots',
  AlternativeDomain = 'alternative_domain',
  IndependentDomain = 'independent_domain',
  CourseCreator = 'course_creator',
  SignUp = 'sign_up',
  RealEstate = 'real_estate',
}

export enum SubscriptionPlanInterval {
  day = 'day',
  month = 'month',
  year = 'year',
}

export enum SubscriptionPlanType {
  plan = 'plan',
  commentChatAddOn = 'commentChatAddOn',
  modelMeAddOn = 'modelMeAddOn',
  taxProduct = 'taxProduct',
  popStoreAiEchoMeAddOn = 'popStoreAiEchoMeAddOn',
}

export enum SaasSubscriptionBasePlanType {
  BEAUTIFY = 'BEAUTIFY',
  COMMUNITY = 'COMMUNITY',
  EXPERT = 'EXPERT',
  CREATOR_PRO = 'CREATOR PRO',
  PRO = 'PRO',
  REAL_ESTATE_STARTER = 'REAL ESTATE STARTER',
  REAL_ESTATE_PRO = 'REAL ESTATE PRO',
  REAL_ESTATE_STAR = 'REAL ESTATE STAR',

  EXPERT_COACH_LAUNCH = 'EXPERT COACH LAUNCH',
  EXPERT_COACH_PRO = 'EXPERT COACH PRO',
  EXPERT_COACH_AUTHORITY = 'EXPERT COACH AUTHORITY',

  REAL_ESTATE_AGENT = 'REAL ESTATE AGENT',
  REAL_ESTATE_PRO_AGENT = 'REAL ESTATE PRO AGENT',
  REAL_ESTATE_TOP_PRODUCER = 'REAL ESTATE TOP PRODUCER',

  BEAUTY_LIFESTYLE_CREATOR = 'BEAUTY LIFESTYLE CREATOR',
  BEAUTY_LIFESTYLE_PRO = 'BEAUTY LIFESTYLE PRO',
  BEAUTY_LIFESTYLE_ICON = 'BEAUTY LIFESTYLE ICON',

  AI_PDF_CREATOR = 'AI PDF CREATOR',
  ECHO_ME_AI_AGENT = 'ECHO-ME AI Agent',

  /** The $0 tier of the ECHO-ME line: a real Stripe subscription at no charge. */
  ECHO_ME_FREE = 'ECHO-ME FREE',
  ECHO_ME_SOLO = 'ECHO-ME SOLO',
  ECHO_ME_SPARK = 'ECHO-ME SPARK',
  ECHO_ME_SURGE = 'ECHO-ME SURGE',
}

/**
 * Which price line a plan belongs to.
 *
 * ABSENT IS THE MAIN LINE. Every plan that exists today is on it, so nothing
 * has to be backfilled and a caller that asks for no track keeps getting
 * exactly what it gets now. A value here marks the plan as belonging to a
 * PARALLEL line instead: the same tiers with the same entitlements at a
 * different amount, shown only to creators who arrived through that line's own
 * pricing URL.
 *
 * Deliberately NOT expressed by minting a second set of
 * {@link SaasSubscriptionBasePlanType} values. That enum answers "which tier
 * is this", and drives the plan's precedence, its community-channel limits,
 * its paid-trial eligibility and the name on its emails — all of which the two
 * lines share. A second set would duplicate every one of those entries and
 * leave behind an invariant ("the copies must behave identically forever")
 * that nothing in the type system can check.
 *
 * ASKING FOR A TRACK ASKS FOR THAT LINE AND NOTHING ELSE — the add-ons and the
 * cross-cutting plans are left out too, even though they carry no track of
 * their own, because a parallel line's page sells that line's tiers and is not
 * the main catalogue. Asking for no track is unchanged and still returns
 * everything on the main line, add-ons included, since absent matches absent.
 */
export enum SubscriptionPricingTrack {
  /** The Business Coach pricing page: the coaching tiers at a higher amount. */
  BusinessCoach = 'business_coach',
}

/**
 * The LADDER a base plan type belongs to — the set of tiers a creator actually
 * chooses between, which is the audience the plan was built for.
 *
 * Named by the ladder rather than by {@link PopStoreCreatorBusinessType}, and
 * deliberately: the two nearly agree (`REAL_ESTATE` ↔ `RealEstate`, `ECHO_ME` ↔
 * `AIEcho`, `EXPERT_COACH` ↔ `CreatorCoach`, `BEAUTY_LIFESTYLE` ↔
 * `BeautyFashionAffiliate`) but not quite — `AI_PDF` is sold to no business
 * type at all, and a business type with no ladder of its own falls to `CORE`.
 * A name claiming the mapping is exact would be wrong on the row that matters.
 *
 * It exists because a plan's NAME does not identify it: four different ladders
 * ship a tier called "Pro", so any surface listing plans flat — the admin's
 * per-plan paid-trial selection is the live case — shows four identical rows
 * with different prices and no way to tell which is which.
 */
export enum SaasSubscriptionPlanFamily {
  /** The original ladder, sold to a creator who declared nothing more specific. */
  CORE = 'CORE',
  REAL_ESTATE = 'REAL_ESTATE',
  EXPERT_COACH = 'EXPERT_COACH',
  BEAUTY_LIFESTYLE = 'BEAUTY_LIFESTYLE',
  ECHO_ME = 'ECHO_ME',
  AI_PDF = 'AI_PDF',
}

/**
 * Which ladder each base plan type sits on.
 *
 * EXHAUSTIVE by type (`Record`, not `Partial<Record>`), so adding a plan type to
 * {@link SaasSubscriptionBasePlanType} fails to compile until it is filed. A
 * lookup that could answer `undefined` would instead put a new plan silently
 * into whatever an "unknown" group is called, which is exactly the flat list
 * this replaces.
 *
 * The RETIRED real-estate names sit on the same ladder as the ones that
 * replaced them (`normalizeRealEstatePlanType` in popstore-next maps STARTER →
 * AGENT, PRO → PRO_AGENT, STAR → TOP_PRODUCER): they are the same product to
 * the same audience, and splitting them would file a creator's live plan apart
 * from the plan they would upgrade to.
 */
export const kSaasSubscriptionPlanFamilyByBaseType: Record<
  SaasSubscriptionBasePlanType,
  SaasSubscriptionPlanFamily
> = {
  [SaasSubscriptionBasePlanType.BEAUTIFY]: SaasSubscriptionPlanFamily.CORE,
  [SaasSubscriptionBasePlanType.COMMUNITY]: SaasSubscriptionPlanFamily.CORE,
  [SaasSubscriptionBasePlanType.EXPERT]: SaasSubscriptionPlanFamily.CORE,
  [SaasSubscriptionBasePlanType.CREATOR_PRO]: SaasSubscriptionPlanFamily.CORE,
  [SaasSubscriptionBasePlanType.PRO]: SaasSubscriptionPlanFamily.CORE,

  [SaasSubscriptionBasePlanType.REAL_ESTATE_STARTER]:
    SaasSubscriptionPlanFamily.REAL_ESTATE,
  [SaasSubscriptionBasePlanType.REAL_ESTATE_PRO]:
    SaasSubscriptionPlanFamily.REAL_ESTATE,
  [SaasSubscriptionBasePlanType.REAL_ESTATE_STAR]:
    SaasSubscriptionPlanFamily.REAL_ESTATE,
  [SaasSubscriptionBasePlanType.REAL_ESTATE_AGENT]:
    SaasSubscriptionPlanFamily.REAL_ESTATE,
  [SaasSubscriptionBasePlanType.REAL_ESTATE_PRO_AGENT]:
    SaasSubscriptionPlanFamily.REAL_ESTATE,
  [SaasSubscriptionBasePlanType.REAL_ESTATE_TOP_PRODUCER]:
    SaasSubscriptionPlanFamily.REAL_ESTATE,

  [SaasSubscriptionBasePlanType.EXPERT_COACH_LAUNCH]:
    SaasSubscriptionPlanFamily.EXPERT_COACH,
  [SaasSubscriptionBasePlanType.EXPERT_COACH_PRO]:
    SaasSubscriptionPlanFamily.EXPERT_COACH,
  [SaasSubscriptionBasePlanType.EXPERT_COACH_AUTHORITY]:
    SaasSubscriptionPlanFamily.EXPERT_COACH,

  [SaasSubscriptionBasePlanType.BEAUTY_LIFESTYLE_CREATOR]:
    SaasSubscriptionPlanFamily.BEAUTY_LIFESTYLE,
  [SaasSubscriptionBasePlanType.BEAUTY_LIFESTYLE_PRO]:
    SaasSubscriptionPlanFamily.BEAUTY_LIFESTYLE,
  [SaasSubscriptionBasePlanType.BEAUTY_LIFESTYLE_ICON]:
    SaasSubscriptionPlanFamily.BEAUTY_LIFESTYLE,

  [SaasSubscriptionBasePlanType.ECHO_ME_AI_AGENT]:
    SaasSubscriptionPlanFamily.ECHO_ME,
  [SaasSubscriptionBasePlanType.ECHO_ME_FREE]:
    SaasSubscriptionPlanFamily.ECHO_ME,
  [SaasSubscriptionBasePlanType.ECHO_ME_SOLO]:
    SaasSubscriptionPlanFamily.ECHO_ME,
  [SaasSubscriptionBasePlanType.ECHO_ME_SPARK]:
    SaasSubscriptionPlanFamily.ECHO_ME,
  [SaasSubscriptionBasePlanType.ECHO_ME_SURGE]:
    SaasSubscriptionPlanFamily.ECHO_ME,

  [SaasSubscriptionBasePlanType.AI_PDF_CREATOR]:
    SaasSubscriptionPlanFamily.AI_PDF,
};

/**
 * The ladder a plan sits on, or `undefined` when its `basePlanType` is absent or
 * is a value this build does not know.
 *
 * UNDEFINED rather than a `CORE` default: a plan type the API has and this
 * bundle does not is a deployment gap, and filing it under the original ladder
 * would hide it among plans it has nothing to do with. A caller renders it
 * apart, under its raw type.
 */
export function saasSubscriptionPlanFamily(
  basePlanType: string | undefined | null,
): SaasSubscriptionPlanFamily | undefined {
  if (basePlanType == null) return undefined;
  return kSaasSubscriptionPlanFamilyByBaseType[
    basePlanType as SaasSubscriptionBasePlanType
  ];
}

/**
 * Display order for the ladders, and the heading each is shown under.
 *
 * ECHO-ME FIRST, real estate second — the two the platform is actually sold on,
 * and the two an operator configures. `Creator` and `AI PDF` go LAST: the first
 * is the original ladder, now mostly legacy tiers nobody assigns, and the second
 * is a single plan.
 *
 * Deliberately NOT the enum's order (roughly the order the ladders were built)
 * and not alphabetical: a list ordered by history puts the live products below
 * the legacy ones, and every screen reading this then buries the rows that get
 * touched. Reorder here and every consumer follows.
 */
export const kOrderedSaasSubscriptionPlanFamilies: {
  family: SaasSubscriptionPlanFamily;
  label: string;
}[] = [
  { family: SaasSubscriptionPlanFamily.ECHO_ME, label: 'ECHO-ME' },
  { family: SaasSubscriptionPlanFamily.REAL_ESTATE, label: 'Real estate' },
  { family: SaasSubscriptionPlanFamily.EXPERT_COACH, label: 'Creator coach' },
  {
    family: SaasSubscriptionPlanFamily.BEAUTY_LIFESTYLE,
    label: 'Beauty & lifestyle',
  },
  { family: SaasSubscriptionPlanFamily.CORE, label: 'Creator' },
  { family: SaasSubscriptionPlanFamily.AI_PDF, label: 'AI PDF' },
];

export interface SaasSubscriptionPromotion extends ModelBase {
  couponId: string;
  subscriptionPlanIds: string[];
  name?: string;
  scene?: SaasSubscriptionScene;
  tags?: string[];
  isSceneDefault?: boolean;
  isDefault?: boolean;
  headerImageUrl?: string;
  headerContent?: string;
  offerContent?: string;
  sceneImageUrl?: string;
  cancelBtnText?: string;
  confirmBtnText?: string;
  isDeleted?: boolean;
}

// ---------------------------------------------------------------------------
// Coupon campaign
//
// A campaign is a scripted sequence of coupon offers for one scene: the user
// lands, we evaluate the campaign's criteria against them, and then run stages
// as their behaviour triggers them — escalating the discount, or changing how
// the offer reaches them, rather than showing one flat promotion.
//
// This is the generic form of {@link SaasSubscriptionPromotion}: a promotion is
// one coupon pinned to a scene, a campaign is a branch tree over time.
// ---------------------------------------------------------------------------

/**
 * Where in the app a campaign runs. The scene IS the page contract — it names
 * both the app context and the surface the offer renders on, so a campaign
 * carries no separate route or path field.
 */
export enum SaasSubscriptionCouponCampaignScene {
  NewSubscription = 'new_subscription',
  UpgradeSubscription = 'upgrade_subscription',
}

/**
 * Campaign-level eligibility. Fields are ANDed, values within one field's array
 * are ORed, and an absent field constrains nothing — so `{}` and `undefined`
 * both mean "everyone in the scene".
 */
export interface SaasSubscriptionCouponCampaignCriteria {
  /**
   * Marketing params the user's signup URL must have carried, keyed by the RAW
   * snake_case param name (`utm_campaign`, `utm_source`, `utm_regintent`, …)
   * and matched case-insensitively against the user's
   * `onboardingMarketingCapture`. Deliberately an open map rather than named
   * fields — a new ad param ships without a model change, the same reason
   * `utm_eventname` values come from `popstore_event_names` config and not an
   * enum. See {@link CampaignUrl} for the params the admin's URL builder emits.
   */
  utmParams?: Record<string, string[]>;
  businessTypes?: PopStoreCreatorBusinessType[];
  /**
   * True = only users who have never held a paid subscription. Absent or false
   * places no constraint — it does NOT mean "returning users only".
   */
  firstSubscriptionOnly?: boolean;
  /**
   * Explicit allowlist, for dry-running a campaign against staff accounts
   * before opening it up. Absent = no allowlist, not an empty one.
   */
  userIds?: string[];
}

/** What makes a stage run. */
export enum SaasSubscriptionCouponCampaignTriggerType {
  Landed = 'landed',
  Idled = 'idled',
  ActionTaken = 'action_taken',
  Left = 'left',
}

/**
 * Which departure signal fires a
 * {@link SaasSubscriptionCouponCampaignTriggerType.Left} stage.
 */
export enum SaasSubscriptionCouponCampaignExitSignal {
  /** Tab backgrounded — the recoverable one, the user may come back. */
  PageHidden = 'page_hidden',
  /** Browser or tab closed. */
  PageUnload = 'page_unload',
  /** Navigated away from the scene while staying in the app. */
  SceneLeft = 'scene_left',
}

export type SaasSubscriptionCouponCampaignTrigger =
  | {
      type: SaasSubscriptionCouponCampaignTriggerType.Landed;
      /** Grace period after landing; absent = fire immediately. */
      delaySeconds?: number;
    }
  | {
      type: SaasSubscriptionCouponCampaignTriggerType.Idled;
      /** Uninterrupted idle time on the scene before the stage runs. */
      idleSeconds: number;
    }
  | {
      type: SaasSubscriptionCouponCampaignTriggerType.ActionTaken;
      /**
       * The client-emitted action name, e.g. `plan_card_clicked`. A free string
       * rather than an enum so the frontend can instrument a new button without
       * a model change.
       */
      actionKey: string;
    }
  | {
      type: SaasSubscriptionCouponCampaignTriggerType.Left;
      /** Absent = any departure signal fires the stage. */
      signals?: SaasSubscriptionCouponCampaignExitSignal[];
    };

/** How a stage's offer reaches the user. */
export enum SaasSubscriptionCouponCampaignReachMethod {
  Inline = 'inline',
  Popup = 'popup',
  Email = 'email',
  EmailFlow = 'email_flow',
}

/**
 * Per-method delivery config.
 * {@link SaasSubscriptionCouponCampaignReachMethod.Inline} and
 * {@link SaasSubscriptionCouponCampaignReachMethod.Popup} are rendered by the
 * client; {@link SaasSubscriptionCouponCampaignReachMethod.Email} and
 * {@link SaasSubscriptionCouponCampaignReachMethod.EmailFlow} are executed by
 * the SERVER, which is why they name a template or a flow rather than UI
 * copy.
 */
export type SaasSubscriptionCouponCampaignReach =
  | {
      method: SaasSubscriptionCouponCampaignReachMethod.Inline;
      /**
       * Named region on the scene's page; absent = the scene's default slot.
       */
      slot?: string;
    }
  | {
      method: SaasSubscriptionCouponCampaignReachMethod.Popup;
      /** Absent = not dismissible; the popup closes on claim or countdown. */
      dismissible?: boolean;
      /** Show the popup once more this session after the user dismisses it. */
      reshowOnDismiss?: boolean;
    }
  | {
      method: SaasSubscriptionCouponCampaignReachMethod.Email;
      /**
       * Doc id in the `emailTemplates` Firestore collection — ONE template,
       * sent once.
       *
       * Not to be confused with `mailTemplates` in lib/mail.ts, the older
       * transactional collection whose documents are addressed by
       * `MailTemplate.name`.
       */
      emailTemplateId: string;
      /**
       * Delay before sending, so a quick tab-switch or a reload doesn't fire a
       * winback email at someone who is still on the page.
       */
      sendAfterSeconds?: number;
    }
  | {
      method: SaasSubscriptionCouponCampaignReachMethod.EmailFlow;
      /**
       * `EmailFlow.flowId` — a multi-step flow authored under the admin's
       * Marketing → Email Flows and owned by the ai-service, NOT a Firestore
       * doc id.
       *
       * Choose this over
       * {@link SaasSubscriptionCouponCampaignReachMethod.Email} when the
       * winback is a sequence rather than a single send. The flow carries its
       * own waits and conditions, so it takes no `sendAfterSeconds` here —
       * pacing belongs to the flow's own steps.
       */
      emailFlowId: string;
    };

/**
 * One branch of a campaign: the coupon offered, when it runs, how it reaches
 * the user, and how long it lasts.
 */
export interface SaasSubscriptionCouponCampaignStage {
  /**
   * Stable id, unique within the campaign. Analytics and the client's
   * "already seen" bookkeeping key off it, so it must NOT be the array
   * index — that shifts whenever an admin reorders stages.
   */
  key: string;
  /** Human label for the admin list; not shown to the user. */
  name?: string;
  /** The `saasSubscriptionCoupons` doc id offered when this stage runs. */
  couponId: string;
  trigger: SaasSubscriptionCouponCampaignTrigger;
  reach: SaasSubscriptionCouponCampaignReach;
  /**
   * Evaluation order, ASC. The lowest-sort stage is the campaign's default
   * stage — the one that runs on landing.
   */
  sort: number;
  /**
   * How long the offer stays claimable once the stage has run; absent = until
   * the scene ends.
   *
   * This is also what a `popup` reach counts down: the popup previously had a
   * `countdownSeconds` of its own, which said the same thing in a second place
   * and could disagree with this one — a countdown that outlived the offer, or
   * an offer that outlived the countdown it was advertised with.
   *
   * ON AN EMAIL STAGE IT IS REQUIRED, AND IT IS THE ONLY ONE OF THESE THE
   * SERVER ENFORCES. The send mints a coupon for that one creator with
   * `redeemExpireTime = sent + expiresInSeconds`, so the window runs from when
   * they were mailed rather than from any instant shared with everybody else.
   * A `popup` or `inline` stage still just renders it.
   *
   * Keep it comfortably longer than a checkout. Validity is read from
   * Firestore when the request starts but Stripe is called several async hops
   * later, and a coupon that expires in between is refused by Stripe as
   * `This coupon has expired` — which the subscribe path reports to the
   * creator as a card problem, not a coupon one. Hours are safe; seconds are
   * not.
   */
  expiresInSeconds?: number;
  /**
   * Cap on how many times this stage may run for one user; absent = uncapped.
   */
  maxImpressions?: number;
  /**
   * Freeform UI/UX configuration for this stage — text copy, imagery, theme,
   * placement hints — shaped by whatever the stage's {@link reach} method
   * renders. Deliberately untyped: the copy a popup needs and the variables an
   * email template takes have little in common, and the client owns both, so
   * pinning a schema here would force a model change for every new knob.
   */
  meta?: Record<string, unknown>;
}

// saasSubscriptionCouponCampaigns/{autoId}
//
// Worked example — a 30%-off Instagram ad landing an AI Echo signup on the
// plan picker:
//
//   scene: new_subscription
//   criteria: {
//     utmParams: { utm_source: ['instagram'], utm_campaign: ['ig_30off'] },
//     businessTypes: [PopStoreCreatorBusinessType.AIEcho],
//     firstSubscriptionOnly: true,
//   }
//   stages:
//     { key: 'initial',    sort: 0, couponId: <30% off>,
//       trigger: { type: landed },
//       reach:   { method: inline } }
//     { key: 'idle_popup', sort: 1, couponId: <50% off>, expiresInSeconds: 60,
//       trigger: { type: idled, idleSeconds: 15 },
//       reach:   { method: popup, dismissible: true } }
//     { key: 'winback',    sort: 2, couponId: <50% off>,
//       trigger: { type: left, signals: [page_unload, scene_left] },
//       reach:   { method: email, emailTemplateId: 'couponWinback',
//                  sendAfterSeconds: 300 } }
export interface SaasSubscriptionCouponCampaign extends ModelBase {
  name: string;
  scene: SaasSubscriptionCouponCampaignScene;
  isActive: boolean;
  /**
   * Whether a signed-out visitor can be offered this campaign.
   *
   * Required, not optional, because the safe default is not obvious: a campaign
   * silently defaulting to `true` would leak offers to logged-out traffic,
   * while defaulting to `false` would silently kill an acquisition campaign
   * aimed at exactly that traffic. Every campaign has to say which it is.
   *
   * When true, note that the parts of {@link criteria} needing an account —
   * `businessTypes`, `firstSubscriptionOnly`, `userIds` — cannot be evaluated
   * for an anonymous visitor. Such a campaign should target on `utmParams`
   * alone, or it will never match one.
   */
  allowAnonymousUsers: boolean;
  /** Absent = live from creation. */
  startsAt?: AppDate;
  /** Absent = runs until `isActive` is turned off. */
  endsAt?: AppDate;
  /**
   * Highest wins when several campaigns match the same user in the same scene.
   * Absent sorts below any explicit priority.
   */
  priority?: number;
  /** Absent = every user who reaches the scene is eligible. */
  criteria?: SaasSubscriptionCouponCampaignCriteria;
  /**
   * Evaluated in `sort` order. MUST hold at least one stage — the lowest-sort
   * entry is the default stage. Not enforced by the type; validate on write.
   */
  stages: SaasSubscriptionCouponCampaignStage[];
  /** Internal notes for admin (backend only). */
  notes?: string;
  createdBy: string;
}

/**
 * Where one user's run of one stage got to.
 *
 * Only server-delivered reach methods produce these — a stage the client
 * renders itself needs no server record. Today that means
 * {@link SaasSubscriptionCouponCampaignReachMethod.Email}.
 */
export enum SaasSubscriptionCouponCampaignDeliveryStatus {
  /** Claimed and queued; the send is waiting out `reach.sendAfterSeconds`. */
  Scheduled = 'scheduled',
  /**
   * A worker has picked the job up. Distinct from `scheduled` so a job
   * re-delivered after its worker died is recognised and dropped rather than
   * sending twice.
   */
  Sending = 'sending',
  Sent = 'sent',
  /** The send was attempted and refused or errored; see `reason`. */
  Failed = 'failed',
  /**
   * The offer stopped being warranted between the trigger and the send — the
   * user subscribed, the campaign was deactivated, the stage was edited away.
   * Not a failure.
   */
  Skipped = 'skipped',
}

// saasSubscriptionCouponCampaignDeliveries/{campaignId}__{userId}__{stageKey}
//
// The composite document id is the idempotency key: the `left` trigger fires
// on both `page_hidden` and `page_unload`, and a reload fires it again, so the
// same stage is reported several times for one visit and only the first may
// schedule a send.
//
// Top-level rather than a subcollection of the campaign because
// `deleteCampaign` hard-deletes and Firestore does not cascade — nested, these
// would survive their parent while being reachable only by collection group.
//
// `campaignId`, `userId` and `stageKey` are duplicated out of the id on
// purpose: the id itself is unqueryable, and the BigQuery mirror is useless
// without them.
export interface SaasSubscriptionCouponCampaignDelivery extends ModelBase {
  campaignId: string;
  userId: string;
  stageKey: string;
  reachMethod: SaasSubscriptionCouponCampaignReachMethod;
  status: SaasSubscriptionCouponCampaignDeliveryStatus;
  /**
   * How many times this stage has been scheduled for this user, checked
   * against `stage.maxImpressions`.
   *
   * Increments only when leaving a terminal status, never on a repeat report
   * of a run already in flight — otherwise the duplicate `page_hidden` report
   * would consume the single impression a `maxImpressions: 1` stage has, and
   * the real one would be dropped.
   */
  count: number;
  /**
   * When the send is due. Also the staleness key: a job whose `scheduledAt`
   * no longer matches this has been superseded by a later trigger and must
   * not send.
   */
  scheduledFor: AppDate;
  jobId?: string;
  /**
   * The coupon actually offered, snapshotted at send time. Not derivable from
   * the campaign afterwards — an admin can repoint the stage.
   */
  couponId?: string;
  /** The `mails` doc the send produced, for tracing the delivery. */
  mailId?: string;
  /**
   * Why a `failed` or `skipped` outcome happened. Free text: the causes span
   * this domain's own checks and whatever `FlowSendResult.reason` reports.
   */
  reason?: string;
  sentAt?: AppDate;
}

// saasSubscriptionCouponCampaignClaims/{claimToken}
//
// One document per email sent, and **the document id is the claim token** —
// the opaque string the email's CTA carries. That is why it is random: the id
// travels in a link that sits in an inbox for weeks and is forwardable, so it
// has to be unguessable, and resolving a claim is then a `get` rather than a
// query.
//
// Not a field on the delivery, which is where it started. A delivery is keyed
// `{campaignId}__{userId}__{stageKey}` because addressing it deterministically
// is what stops two simultaneous departure beacons producing two sends — so it
// is one record per (campaign, user, stage), and it has room for exactly one
// token. A claim is per *email*, and a stage sends again every time it is
// retriggered. Both ways of fitting the second send into one record are wrong:
// reuse the first token and the later email advertises a deadline that has
// already passed (the resend cooldown and `expiresInSeconds` both default to
// 24 hours, so it always has), or replace it and every earlier email dies the
// moment a later one goes out.
//
// Every send mints a fresh token and writes one of these. Nothing ever
// rewrites or invalidates an earlier one, so an older email keeps working
// until *its own* expiry however many later ones went out.
//
// Self-sufficient on purpose: the claim endpoint checks ownership and loads
// the campaign from this record alone and never reads the delivery.
export interface SaasSubscriptionCouponCampaignClaim extends ModelBase {
  /** The send this claim belongs to, for tracing back to the bookkeeping. */
  deliveryId: string;
  campaignId: string;
  stageKey: string;
  /**
   * Who the email went to. The claim endpoint refuses an authenticated caller
   * whose uid is anyone else, which is what makes forwarding the link inert.
   */
  userId: string;
  /**
   * The coupon minted for *this* send — not whatever the stage points at when
   * the claim arrives, which an admin can have repointed since.
   */
  couponId: string;
  /**
   * When the CTA stops working: `sentAt + stage.expiresInSeconds`.
   *
   * The same instant as the minted coupon's `redeemExpireTime`, so the link
   * and checkout cannot disagree about the deadline. Absent when the stage set
   * no `expiresInSeconds` and no default applied.
   */
  expiresAt?: AppDate;
  /** First successful claim. Informational — claiming is idempotent. */
  claimedAt?: AppDate;
}

export interface SaasSubscriptionRedeemedCoupon {
  couponId: string;
  coupon: SaasSubscriptionCoupon;
  userId: string;
  createdAt: AppDate;
  subscriptionId: string;
  linkInBioUrl?: string;
  /**
   * How this seller came to get this coupon. Absent on every redemption
   * written before the field existed, so a rule that has to hold for existing
   * sellers cannot be built on this alone — see the plan-coupon eligibility
   * check, which falls back to matching `couponId`.
   */
  appliedVia?: SaasSubscriptionCouponAppliedVia;
}

export interface SaasSubscriptionRefund {
  paymentIntentId: string;
  invoiceId?: string;
  reason: string;
  appliedAt: AppDate;
  refundedAmount: number;
  status: string | null;
  currency: string;
  failureReason?: string;
}

/**
 * One line of the muted copy under a feature row's title: the text, and its
 * own glyph.
 *
 * The row carries an icon and so does every line under it — two levels, on
 * purpose. The row's icon marks the feature; a line's icon marks that line.
 */
export interface SubscriptionPlanFeatureDescriptionItem {
  title: string;
  /** Phosphor icon name, PascalCase — same contract as the row's `icon`. */
  icon?: string;
}

/**
 * One entry of a plan card's rich feature list: the title line plus the smaller
 * copy under it (see the pricing page's feature rows — icon, bold title,
 * muted description lines).
 */
export interface SubscriptionPlanFeatureItem {
  /**
   * Phosphor icon name for the row's glyph, PascalCase ("CalendarDots").
   * Optional — a row without one renders the client's default check. A NAME, not
   * a URL or an SVG: both the admin and the storefront ship @phosphor-icons/react,
   * so the name is the whole contract and nothing binary is stored.
   */
  icon?: string;
  title: string;
  /**
   * The muted copy under the title, one entry per line, each with its own
   * glyph — the admin authors the lines, the client renders them in order and
   * never re-splits them. An empty array means an admin cleared the copy;
   * absent means nobody authored any.
   *
   * This WAS one string, and rows written before the change still hold one —
   * in the plan doc, and in the copy snapshotted into every seller
   * subscription at subscribe time, which is never refreshed. The server
   * normalises every `featuresList` it emits (`normalizeStoredFeatureRows` in
   * `plan-features.ts`) into this shape, so this type describes what the API
   * serves, not what happens to be on disk.
   */
  description?: SubscriptionPlanFeatureDescriptionItem[];
  /** When true, the client renders this row ahead of the unpinned ones. */
  pinned?: boolean;
  /**
   * Which tier the row belongs to, as a number the client groups or badges by.
   * The server does not interpret it and derives no entitlement from it.
   *
   * `null` is an admin that CLEARED the tier; absent is a row that never had
   * one. Both render untiered, but they are stored differently for the same
   * reason the rest of this card is — the server keeps what the admin did.
   * `0` is a legitimate tier, so read this with a type check, never a
   * truthiness one.
   */
  tier?: number | null;
  /**
   * UTM registration-intent tag for the row's call to action — the value the
   * client puts in the signup link's query string so a conversion can be
   * attributed to this row.
   *
   * A URL PARAMETER, NOT DISPLAY COPY, so it is NOT reduced to the markup
   * allowlist the copy fields are — it is stored as the admin typed it, only
   * trimmed. Same treatment `CampaignUrl.utmRegintent` gets, deliberately: a
   * server that silently drops a tag it dislikes misattributes conversions
   * with no error anywhere, which is worse than storing an odd one. The
   * client is what has to encode it when it builds the link.
   */
  utmRegintent?: string;
}

export interface SubscriptionPlan {
  productId: string;
  productName: string;
  productSlug: string;
  active: boolean;
  amount: number;
  discountAmount?: number;
  billingScheme: string;
  interval: SubscriptionPlanInterval;
  intervalCount: number;
  planType: SubscriptionPlanType;
  commentChatContactCount?: number;
  modelMePhotoCount?: number;
  aiEchoMeCredit?: number;
  modelMeOutfitsCount?: number;
  includeLinkInBioSite?: boolean;
  includeProductCatalog?: boolean;
  subscriptionItemId?: string;
  description: string;
  /**
   * The card's feature rows as title + description pairs — the structured
   * alternative to the comma-joined `description` bullets. A client that
   * renders rich rows reads this; one that has not switched keeps splitting
   * `description`.
   *
   * Presentation precedence when both are set is the CLIENT's:
   * `rawHtmlFeatures` ?? `featuresList` ?? `description`. The server stores
   * what the admin authored and never derives one shape from another — these
   * are different copy, not the same copy in two encodings.
   */
  featuresList?: SubscriptionPlanFeatureItem[];
  /**
   * Escape hatch: the card's feature area as one admin-authored HTML fragment,
   * for layouts the structured list cannot express. Empty string means an admin
   * cleared it; absent means nobody has authored one.
   */
  rawHtmlFeatures?: string;
  couponId?: string;
  isHidden?: boolean;
  commissionFeeRate?: {
    digitalOrder?: number;
    meetingSlotOrder?: number;
    courseOrder?: number;
    tippingOrder?: number;
  };
  /** When true, waive commission fees for all PopStore non-physical orders */
  waivePopStoreCommissionFee?: boolean;
  // subscription plans order by the sort ASC
  sort?: number;
  annualToMonthlyAmount?: number;
  originMonthlyAmount?: number;
  basePlanType?: SaasSubscriptionBasePlanType;
  planLevel?: number;
  trialPeriodDays?: number;
  businessType?: PopStoreCreatorBusinessType;
  /** Internal notes for admin (backend only) */
  notes?: string;
  /** Stripe coupon ID for paid trial discount (amount off = paid trial charge) */
  paidTrialCouponId?: string;
  /** Amount in dollars to charge upfront for paid trial */
  paidTrialAmount?: number;
  /**
   * How long the PAID trial runs, in days, before the first invoice.
   *
   * Separate from {@link trialPeriodDays}, which is the FREE trial's length:
   * the two offers are configured independently and a plan carries both, so
   * changing one must not move the other. Absent falls back to the platform
   * default the API applies (15 days), so a plan that predates this field
   * still offers a paid trial of the current length.
   */
  paidTrialPeriodDays?: number;
  /**
   * The price line this plan is sold on; absent means the main line. See
   * {@link SubscriptionPricingTrack}.
   *
   * Copied into `SellerSubscription.plans` when a seller subscribes, which is
   * how the upgrade list knows which line they are already on without being
   * told. Inert there — nothing reads it to decide an entitlement.
   */
  pricingTrack?: SubscriptionPricingTrack;
}

export enum ScheduledCouponTrigger {
  /**
   * Apply once the subscription has left its trial, whichever way it did —
   * the trial-end invoice, an upgrade, an add-on purchase or convert-now all
   * end in one paid invoice after which the status is no longer `trialing`.
   */
  TrialEnd = 'trial_end',
  /** Apply after the first paid invoice whose period starts at or after `applyFrom`. */
  Date = 'date',
}

export enum ScheduledCouponStatus {
  Pending = 'pending',
  Applied = 'applied',
  /** Stripe refused the coupon (expired, deleted). `failedReason` says why. */
  Failed = 'failed',
  /** The subscription was cancelled before the trigger fired. */
  Cancelled = 'cancelled',
}

/**
 * A coupon the seller has accepted that is attached to the Stripe
 * subscription later, by the `invoice.payment_succeeded` handler, rather than
 * at acceptance.
 *
 * Why later: a coupon sits on the main plan item, and a paid trial's
 * trial-end invoice is a prorated plan line plus a balance item. A repeating
 * coupon attached mid-trial counts its months from then and covers one
 * invoice too many; a `once` coupon would spend itself on the prorated line
 * and leave the balance item at full price, which is no free month. Attaching
 * right after that invoice is paid lands the discount on the first full
 * invoice.
 *
 * The seller's acceptance is recorded in `saasSubscriptionRedeemedCoupons`
 * at acceptance time, like any other redemption; this entry only tracks the
 * attach. Done entries stay with their status for tracing — the array is a
 * schedule, not a queue.
 */
export interface ScheduledSubscriptionCoupon {
  /** `saasSubscriptionCoupons` doc id, which is also the Stripe coupon id. */
  couponId: string;
  trigger: ScheduledCouponTrigger;
  /** Required for `trigger: date`; ignored otherwise. */
  applyFrom?: AppDate;
  status: ScheduledCouponStatus;
  /** Free text for whoever reads the document, e.g. which offer scheduled it. */
  note?: string;
  createdAt: AppDate;
  appliedAt?: AppDate;
  failedReason?: string;
  /**
   * The coupon the attach displaced. Attaching replaces the plan item's
   * discounts rather than stacking, so a standing discount the seller had is
   * gone after this and does not come back when the new one runs out; this
   * keeps a record of which one it was.
   */
  replacedCouponId?: string;
}

export interface SellerSubscription extends ModelBase {
  paymentIntentId: string;
  defaultPaymentMethod: string;
  status: SubscriptionStatus;
  /**
   * Either charge_automatically, or send_invoice. When charging automatically, Stripe will attempt to pay this subscription at the end of the cycle using the default source attached to the customer.
   * When sending an invoice, Stripe will email your customer an invoice with payment instructions and mark the subscription as active.
   */
  collectionMethod: string;
  /**
   * The time at which the subscription was created in Stripe
   * If the subscription has been canceled, and create a new one, this is the time of the new one.
   */
  initialStartDate: AppDate;
  /**
   * If the subscription has a trial, the end of that trial. (Will charge immediately if the subscription was created with a trial_end date in the past.)
   */
  trialEnd: AppDate | null;
  /**
   * Start of the current period that the subscription has been invoiced for.
   */
  currentPeriodStart: AppDate;
  /**
   * End of the current period that the subscription has been invoiced for. At the end of this period, a new invoice will be created.
   */
  currentPeriodEnd: AppDate;
  /**
   * If the subscription has been canceled, the date of that cancellation.
   */
  cancelledAt: AppDate | null;
  /**
   * Boolean indicating whether this subscription should cancel at the end of the current period.
   */
  cancelAtPeriodEnd?: boolean;
  cancellationAppliedAt: AppDate | null;
  /**
   * The seller's choice when they cancelled (POP-364). `Cancel` stops
   * `fallBackToFreePlan` putting them on the Free plan when this subscription
   * ends; anything else falls back as before.
   */
  cancelIntent?: SaasSubscriptionCancelIntent;
  lastInvoiceId: string;
  plans: (SubscriptionPlan & { priceId: string })[];
  price: number;
  discountedPrice?: number;
  taxAmount: number;
  coupon?: SaasSubscriptionCoupon & { id: string };
  /** Coupons accepted during a trial, attached once it ends. See {@link ScheduledSubscriptionCoupon}. */
  couponSchedule?: ScheduledSubscriptionCoupon[];
  refunds?: SaasSubscriptionRefund[];  // A subscription may contains multiple payment intents, each refund represents a payment intent that has been refunded
  /**
   * History of previous subscriptions when the seller changes(upgrade only for now) their subscription plan.
   */
  previousSubscriptions?: (SellerSubscription & { id: string; changedAt: AppDate })[];
  paymentMethodDetails?: PaymentMethodDetails;
  trialConvertedAt?: AppDate;
  trialConversionType?: TrialConversionType;
  /** Payment intent ID for the upfront paid trial charge */
  paidTrialPaymentIntentId?: string;
  /** Whether this subscription is a no-credit-card trial (local-only, no Stripe subscription) */
  isNoCreditCardTrial?: boolean;
  /**
   * Paid with a card from `public_config.saas_subscription.test_card_auto_refund`
   * — an internal QA signup. Trials were skipped for it, and a job refunds
   * every invoice of this subscription and cancels it after the configured
   * delay. Set once at creation; never on a real customer's subscription.
   */
  isTestCardSubscription?: boolean;
  /** When the test-card auto-refund job finished for this subscription. */
  testCardAutoRefundedAt?: AppDate;
  /**
   * The win-back coupon issued for this cancelled subscription, once the job
   * scheduled by the cancel has run (see
   * `public_config.saas_subscription.win_back_offer`). Set once; the job
   * reads it back to stay idempotent, and the win-back email takes its code.
   */
  winBackOffer?: SellerSubscriptionWinBackOffer;
}

export interface SellerSubscriptionWinBackOffer {
  /** The `saasSubscriptionCoupons` id, which is also the code. */
  couponId: string;
  issuedAt: AppDate;
}

export enum TrialConversionType {
  PdfWatermarkRemoval = 'pdf-watermark-removal',
}

/**
 * Ref: https://docs.stripe.com/invoicing/overview#workflow-overview
 */
export enum SubscriptionInvoiceStatus {
  draft = 'draft',
  open = 'open',
  paid = 'paid',
  void = 'void',
  uncollectible = 'uncollectible',
}

// Ref: https://docs.stripe.com/api/invoices/object
export interface SubscriptionInvoice extends ModelBase {
  amountDue: number;
  amountPaid: number;
  /**
   * Portion of the paid amount that belongs to a credit add-on purchase
   * (add-on price plus its proportional tax share). Credit packs are
   * non-refundable, so the refundable amount is
   * `amountPaid - creditAddonAmount`. Missing or 0 means the invoice
   * contains no credit add-on; equal to amountPaid means a pure
   * credit-pack invoice.
   */
  creditAddonAmount?: number;
  attemptCount: number;
  collectionMethod: string;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
  /**
   * The time at which the invoice was created in Stripe
   */
  created: AppDate;
  defaultPaymentMethod: string;
  /**
   * The status of the invoice, one of draft, open, paid, uncollectible, or void.
   */
  status: SubscriptionInvoiceStatus;
  paymentMethodDetails?: PaymentMethodDetails;
  /**
   * Whether payment collection for this invoice was attempted.
   * Ref: https://docs.stripe.com/api/invoices/object#invoice_object-attempted
   */
  attempted?: boolean;
  /**
   * The time at which payment will next be attempted (unix timestamp).
   * Null if no further attempts will be made.
   * Ref: https://docs.stripe.com/api/invoices/object#invoice_object-next_payment_attempt
   */
  nextPaymentAttempt?: AppDate | null;
}

export enum AiEchoMeTokenTransactionStatus {
  pending = 'pending',
  completed = 'completed',
  expired = 'expired',
  deducted = 'deducted',
}

export enum AiEchoMeTokenGrantSource {
  /**
   * The credits the cancellation survey's free-month offer pays for the
   * seller's full feedback. Written as a transaction of the subscription so
   * the existing clawbacks — expiry, trial cancellation, refund — cover it
   * without a parallel mechanism.
   */
  RetentionBonus = 'retention_bonus',
}

/**
 * AI Echo-Me token grant transaction
 * For annual subscriptions, tokens are granted monthly instead of all at once
 * Stored in: privateProfiles/{sellerId}/sellerSubscriptions/{subscriptionId}/aiEchoMeTokenTransactions/{transactionId}
 */
export interface AiEchoMeTokenTransaction extends ModelBase {
  /**
   * The month number (1-12) for this grant within the subscription period.
   * A grant with a `grantSource` is outside the plan's monthly series and
   * carries 0 here.
   */
  monthIndex: number;
  /** Total months in the subscription (1 for monthly, 12 for annual) */
  totalMonths: number;
  /** Amount of tokens granted */
  tokenAmount: number;
  /** The date when tokens should be granted */
  grantDate: AppDate;
  /** Status of the token grant */
  status: AiEchoMeTokenTransactionStatus;
  /** The token balance change ID after tokens are granted */
  tokenBalanceChangeId?: string;
  /** The date when tokens were actually granted */
  grantedAt?: AppDate;
  /** When these tokens expire (2 months from grant date) */
  expiresAt?: AppDate;
  /** Amount deducted on expiration/cancellation */
  deductedAmount?: number;
  /** When tokens were deducted */
  deductedAt?: AppDate;
  /**
   * Reason for deduction. `cancelled` is a retention bonus taken back when
   * the subscription it rewarded is cancelled, trial or not.
   */
  deductionReason?: 'expired' | 'trial_cancelled' | 'refunded' | 'cancelled';
  /** Token balance change ID for the deduction */
  deductionBalanceChangeId?: string;
  /** Absent on the plan's own recurring grants; set on grants made for another reason. */
  grantSource?: AiEchoMeTokenGrantSource;
}
